import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ONBOARDING_TEAM_ROLES,
  type OnboardingRunTargetInput,
  type OnboardingTeamRole,
} from '../../../api/onboardingTypes'
import { useOnboardingTeamLookup } from '../../../api/hooks'
import { INPUT_CLASS, SECONDARY_BUTTON_CLASS } from './ui'

// The team half of the single-partner form: no team, a new team the partner
// owns, or an existing team found through one of its owners or admins.
//
// With a team, the run's credits pay that team's wallet — the server pins it
// at the team step — so the choice is stated next to the credits, not only here.

export type TeamChoiceMode = 'none' | 'create' | 'join'
const TEAM_CHOICE_MODES: readonly TeamChoiceMode[] = ['none', 'create', 'join']

export interface TeamChoice {
  mode: TeamChoiceMode
  /** For `create`. */
  name: string
  /** For `join`: the team picked from the lookup, and the partner's role in it. */
  teamId: string | null
  teamName: string | null
  role: OnboardingTeamRole
}

export const NO_TEAM: TeamChoice = { mode: 'none', name: '', teamId: null, teamName: null, role: 'member' }

type TeamFields = Pick<OnboardingRunTargetInput, 'team_mode' | 'team_name' | 'join_team_id' | 'team_role'>

/**
 * The create-run fields for a choice, or null while it is incomplete.
 *
 * Only the fields the mode uses are sent: the server refuses the others rather
 * than dropping them, so a name typed before switching to "join" must not leak.
 */
export function teamChoiceFields(choice: TeamChoice): TeamFields | null {
  if (choice.mode === 'none') return {}
  if (choice.mode === 'create') {
    const name = choice.name.trim()
    return name ? { team_mode: 'create', team_name: name } : null
  }
  return choice.teamId
    ? { team_mode: 'join', join_team_id: choice.teamId, team_role: choice.role }
    : null
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export default function TeamIntake({
  value,
  onChange,
}: {
  value: TeamChoice
  onChange: (next: TeamChoice) => void
}) {
  const { t } = useTranslation('pages')
  const [anchorEmail, setAnchorEmail] = useState('')
  // Looked up on an explicit click, not per keystroke: every lookup reads
  // another customer's team list, so it should be a deliberate act.
  const [lookupEmail, setLookupEmail] = useState<string | null>(null)
  const lookup = useOnboardingTeamLookup(value.mode === 'join' ? lookupEmail : null)
  const anchorValid = EMAIL_RE.test(anchorEmail.trim())
  const lookupError = lookup.error instanceof Error ? lookup.error.message : null

  const setMode = (mode: TeamChoiceMode) => onChange({ ...value, mode, teamId: null, teamName: null })

  return (
    <div className="flex flex-col gap-1.5" data-testid="onboarding-team">
      <span className="text-sm text-foreground">{t('adminOnboarding.team.label')}</span>
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={t('adminOnboarding.team.label')}>
        {TEAM_CHOICE_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={value.mode === mode}
            onClick={() => setMode(mode)}
            className={`h-8 rounded-lg px-3 text-sm ${
              value.mode === mode
                ? 'bg-surface-3 text-foreground'
                : 'text-muted-foreground hover:bg-surface-2'
            }`}
            data-testid={`onboarding-team-mode-${mode}`}
          >
            {t(`adminOnboarding.team.mode.${mode}`)}
          </button>
        ))}
      </div>
      <span className="text-xs font-medium text-muted-foreground">{t('adminOnboarding.team.help')}</span>

      {value.mode === 'create' && (
        <label className="mt-2 flex flex-col gap-1.5">
          <span className="text-sm text-foreground">{t('adminOnboarding.team.nameLabel')}</span>
          <input
            type="text"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            className={INPUT_CLASS}
            data-testid="onboarding-team-name-input"
          />
          <span className="text-xs font-medium text-muted-foreground">
            {t('adminOnboarding.team.createHelp')}
          </span>
        </label>
      )}

      {value.mode === 'join' && (
        <div className="mt-2 flex flex-col gap-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-foreground">{t('adminOnboarding.team.anchorLabel')}</span>
            <div className="flex gap-2">
              <input
                type="email"
                value={anchorEmail}
                onChange={(e) => setAnchorEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && anchorValid) setLookupEmail(anchorEmail.trim().toLowerCase())
                }}
                className={`${INPUT_CLASS} flex-1`}
                data-testid="onboarding-team-anchor-input"
              />
              <button
                type="button"
                disabled={!anchorValid || lookup.isFetching}
                onClick={() => setLookupEmail(anchorEmail.trim().toLowerCase())}
                className={`${SECONDARY_BUTTON_CLASS} h-9`}
                data-testid="onboarding-team-lookup"
              >
                {lookup.isFetching ? t('adminOnboarding.team.lookingUp') : t('adminOnboarding.team.lookup')}
              </button>
            </div>
          </label>

          {lookupError && (
            <p className="text-xs font-medium text-destructive" data-testid="onboarding-team-lookup-error">
              {lookupError}
            </p>
          )}
          {lookup.data && !lookup.data.user_found && (
            <p className="text-xs font-medium text-muted-foreground" data-testid="onboarding-team-no-user">
              {t('adminOnboarding.team.userNotFound', { email: lookup.data.email })}
            </p>
          )}
          {lookup.data && lookup.data.user_found && lookup.data.teams.length === 0 && (
            <p className="text-xs font-medium text-muted-foreground" data-testid="onboarding-team-no-teams">
              {t('adminOnboarding.team.noTeams', { email: lookup.data.email })}
            </p>
          )}
          {lookup.data && lookup.data.teams.length > 0 && (
            <div
              className="flex flex-col gap-1"
              role="radiogroup"
              aria-label={t('adminOnboarding.team.pickLabel')}
              data-testid="onboarding-team-options"
            >
              {lookup.data.teams.map((team) => {
                const full = team.member_count >= team.member_limit
                const selected = value.teamId === team.team_id
                return (
                  <button
                    key={team.team_id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={full}
                    onClick={() => onChange({ ...value, teamId: team.team_id, teamName: team.name })}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                      selected
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border bg-surface-1 text-foreground hover:bg-surface-2'
                    }`}
                    data-testid={`onboarding-team-option-${team.team_id}`}
                  >
                    <span className="flex-1 truncate">{team.name}</span>
                    {team.kind === 'personal' && (
                      <span className="rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground">
                        {t('adminOnboarding.team.personal')}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {t(`adminOnboarding.team.anchorRole.${team.role}`)}
                    </span>
                    <span className={`text-xs ${full ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {full
                        ? t('adminOnboarding.team.full')
                        : t('adminOnboarding.team.members', {
                            count: team.member_count,
                            limit: team.member_limit,
                          })}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {value.teamId && (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-foreground">{t('adminOnboarding.team.roleLabel')}</span>
              <div className="flex gap-1" role="radiogroup" aria-label={t('adminOnboarding.team.roleLabel')}>
                {ONBOARDING_TEAM_ROLES.map((role) => (
                  <button
                    key={role}
                    type="button"
                    role="radio"
                    aria-checked={value.role === role}
                    onClick={() => onChange({ ...value, role })}
                    className={`h-8 rounded-lg px-3 text-sm ${
                      value.role === role
                        ? 'bg-surface-3 text-foreground'
                        : 'text-muted-foreground hover:bg-surface-2'
                    }`}
                    data-testid={`onboarding-team-role-${role}`}
                  >
                    {t(`adminOnboarding.team.role.${role}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
